import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { type EmergencyContact, type ParentDetails } from '~entities/parent';
import { useUpdateParentByStudentId } from '~entities/parent/api/update-parent-by-student-id';

import {
  EmergencyContactForm,
  type EmergencyContactFormSchema,
} from '~features/contacts/parent/emergency-contact-form';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export function EmergencyContactCard({
  contact,
  contactIndex,
  parent,
  studentId,
}: {
  parent: ParentDetails;
  contact: EmergencyContact;
  contactIndex: number;
  studentId: string;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const { mutateAsync: updateParent, isPending } = useUpdateParentByStudentId();

  const onSubmit = async (data: EmergencyContactFormSchema) => {
    try {
      // Only send the emergency contacts array - partial update
      const updatedEmergencyContacts = parent.emergencyContacts.map((c, index) =>
        index === contactIndex
          ? {
              ...c,
              ...data,
            }
          : c,
      );

      await updateParent({
        studentId,
        parentId: parent._id,
        parent: {
          emergencyContacts: updatedEmergencyContacts,
        },
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update parent:', error);
    }
  };

  if (isEditing) {
    return (
      <div className='max-w-sm'>
        <EmergencyContactForm
          onSubmit={onSubmit}
          currentContact={contact}
          setIsEditing={setIsEditing}
          isPending={isPending}
        />
      </div>
    );
  }

  return (
    <Card className='max-w-sm'>
      <CardHeader>
        <div className='flex items-center justify-between gap-2'>
          <CardTitle>Emergency Contact Info</CardTitle>
          <Button
            variant={'ghost'}
            className='-mr-2 p-2'
            onClick={() => {
              setIsEditing(true);
            }}
          >
            <Pencil />
          </Button>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <h3 className='font-semibold'>First Name</h3>
            <p>{contact.firstName}</p>
          </div>
          <div>
            <h3 className='font-semibold'>Last Name</h3>
            <p>{contact.lastName}</p>
          </div>
        </div>
        <div>
          <h3 className='font-semibold'>Phone Number</h3>
          <p>{contact.phone}</p>
        </div>
        <div>
          <h3 className='font-semibold'>Email</h3>
          <p>{contact.emergencyContactEmail}</p>
        </div>
        <div>
          <h3 className='font-semibold'>Authorized for Pickup</h3>
          <p>{contact.authorized ? 'Yes' : 'No'}</p>
        </div>
      </CardContent>
    </Card>
  );
}
